import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Linkedin } from "lucide-react";

const ContattiPage = () => {
  const [emailRevealed, setEmailRevealed] = useState(false);

  // Email obfuscation - split into parts to prevent scraping
  const emailParts = {
    user: "info",
    domain: "metodocontabile",
    tld: "it",
  };

  const handleEmailReveal = () => {
    if (!emailRevealed) {
      setEmailRevealed(true);
    } else {
      // Open email client
      const email = `${emailParts.user}@${emailParts.domain}.${emailParts.tld}`;
      window.location.href = `mailto:${email}`;
    }
  };

  const getDisplayEmail = () => {
    if (emailRevealed) {
      return `${emailParts.user}@${emailParts.domain}.${emailParts.tld}`;
    }
    return "Clicca per rivelare l'email";
  };

  return (
    <div className="min-h-screen bg-brand-background" data-testid="contatti-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-white/60 text-sm font-medium tracking-wider uppercase">
              Contatti
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mt-4 mb-6">
              Parliamo del tuo progetto
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Siamo a tua disposizione per rispondere a qualsiasi domanda.
              Contattaci per una prima consulenza gratuita.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Info Cards */}
            <div className="space-y-6">
              <Card
                className="border-brand-border"
                data-testid="contact-card-address"
              >
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-brand-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-1">
                      Indirizzo
                    </h3>
                    <p className="text-brand-muted">
                      Via Umberto I, 28
                      <br />
                      33038 San Daniele del Friuli (UD)
                      <br />
                      Friuli Venezia Giulia, Italia
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-brand-border"
                data-testid="contact-card-phone"
              >
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-brand-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-1">
                      Telefono
                    </h3>
                    <a
                      href="tel:+390432942144"
                      className="text-brand-primary hover:underline text-lg font-medium"
                      data-testid="phone-link"
                    >
                      0432 942144
                    </a>
                    <p className="text-brand-muted text-sm mt-1">
                      Chiamaci per informazioni immediate
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-brand-border cursor-pointer hover:border-brand-primary transition-colors"
                onClick={handleEmailReveal}
                data-testid="contact-card-email"
              >
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-brand-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-1">
                      Email
                    </h3>
                    <button
                      className={`text-left ${
                        emailRevealed
                          ? "text-brand-primary hover:underline text-lg font-medium"
                          : "text-brand-muted"
                      }`}
                      data-testid="email-reveal-btn"
                    >
                      {getDisplayEmail()}
                    </button>
                    <p className="text-brand-muted text-sm mt-1">
                      {emailRevealed
                        ? "Clicca di nuovo per inviare email"
                        : "Clicca per visualizzare l'indirizzo"}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="border-brand-border"
                data-testid="contact-card-hours"
              >
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-brand-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-1">
                      Orari di Apertura
                    </h3>
                    <div className="text-brand-muted space-y-1">
                      <p>
                        <span className="font-medium">Lunedì - Venerdì:</span>{" "}
                        9:00 - 18:00
                      </p>
                      <p>
                        <span className="font-medium">Sabato - Domenica:</span>{" "}
                        Chiuso
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card className="border-brand-border" data-testid="contact-card-social">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-brand-foreground mb-4">
                    Seguici sui Social
                  </h3>
                  <div className="flex items-center gap-4">
                    <a
                      href="https://www.facebook.com/metodocontabilesrlsandanieledelfriuliud/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center hover:bg-brand-primary hover:text-white transition-colors text-brand-primary"
                      data-testid="contact-social-facebook"
                      aria-label="Facebook"
                    >
                      <Facebook size={24} />
                    </a>
                    <a
                      href="https://www.instagram.com/metodocontabile/?hl=it"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center hover:bg-brand-primary hover:text-white transition-colors text-brand-primary"
                      data-testid="contact-social-instagram"
                      aria-label="Instagram"
                    >
                      <Instagram size={24} />
                    </a>
                    <a
                      href="https://www.instagram.com/metodocontabile/?hl=it"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-brand-secondary rounded-xl flex items-center justify-center hover:bg-brand-primary hover:text-white transition-colors text-brand-primary"
                      data-testid="contact-social-linkedin"
                      aria-label="LinkedIn"
                    >
                      <Linkedin size={24} />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Map */}
            <div>
              <Card className="border-brand-border overflow-hidden h-full">
                <div className="aspect-video md:aspect-auto md:h-full min-h-[400px] bg-brand-secondary relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2770.123456789!2d13.0123456!3d46.1234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477a2c0d6cb6df2f%3A0x5c8a8e8f8c8c8c8c!2sVia%20Umberto%20I%2C%2028%2C%2033038%20San%20Daniele%20del%20Friuli%20UD!5e0!3m2!1sit!2sit!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: "400px" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mappa Metodo Contabile San Daniele del Friuli"
                    className="absolute inset-0"
                    data-testid="google-map"
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-brand-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-foreground mb-4">
              Consulenza anche a distanza
            </h2>
            <p className="text-brand-muted leading-relaxed">
              Grazie alle tecnologie digitali, siamo in grado di seguirti anche
              a distanza. Offriamo consulenza e supporto online per clienti in
              tutta Italia.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContattiPage;
